import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/Toast";
import { formValues, schema } from "./validations";
import {
  ASSIGN_TO,
  ASSIGNMENT_DATE,
  ATTACHMENTS,
  COMMENTS,
  COMMON_AREA,
  DESCRIPTION,
  INCIDENT_CATEGORY,
  INCIDENT_TYPE,
  PRIORITY,
  PROPERTY_ID,
  PROPERTY_SPECIFIC,
  REPORTEE,
  RESPONSIBLE,
  STATUS,
} from "./consts";
import {
  capitalize,
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { routes } from "@/routes/utils";
import {
  useAddIncidentMutation,
  useGetActiveIncidentCategory,
  useGetActiveIncidentTypes,
  useGetIncidentById,
  useGetIncidentCommentHistory,
  useGetPropertiesByCommunity,
  useGetPropertyAssociationList,
  useUpdateIncidentMutation,
} from "@/services";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect, useRef, useState } from "react";
import moment from "moment";

const useAddEdit = () => {
  const { id, action } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const attachmentRef = useRef<any>(null);
  const isView = action == "view";
  const isEdit = action == "edit";
  const isAdd = !id;
  const [pageNumber, setPageNumber] = useState(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [INCIDENT_TYPE]: "",
      [INCIDENT_CATEGORY]: "",
      [PROPERTY_SPECIFIC]: true,
      [COMMON_AREA]: false,
      [ASSIGN_TO]: "",
      [RESPONSIBLE]: "",
      [REPORTEE]: loggedInUserData?.fullName,
      [ASSIGNMENT_DATE]: moment().format("MM/DD/YYYY"),
      [PRIORITY]: "",
      [STATUS]: !id ? "OPEN" : "",
      [ATTACHMENTS]: "",
      [DESCRIPTION]: "",
      [PROPERTY_ID]: "",
      [COMMENTS]: "",
    },
  });
  const [keys, setKey] = useState({
    incidentType: Math.random() * 2,
    incidentCategory: Math.random() * 2,
    responsible: Math.random() * 2,
    priority: Math.random() * 2,
    status: Math.random() * 2,
  });

  const requiredFields = getRequiredFields(schema);

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const { data: incidentCategory, isLoading: isIncidentCategoryLoading } =
    useGetActiveIncidentCategory({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  const { data: incidentType, isLoading: isIncidentTypeLoading } =
    useGetActiveIncidentTypes({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  const { data: propertiesData, isLoading: isLoadingProperties } =
    useGetPropertiesByCommunity({
      communityId: communityData?.communityId as string,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  const { data: incidentData, isLoading: isIncidentDataLoading } =
    useGetIncidentById({
      communityId: communityData?.communityId,
      id: id as string,
      queryParams: {
        enabled: !!id,
      },
    });

  const { data: incidentComments, isLoading: isIncidentCommentsLoading } =
    useGetIncidentCommentHistory({
      communityId: communityData?.communityId,
      id: id as string,
      queryParams: {
        enabled: !!id,
      },
    });

  const breadcrumbData = [
    {
      link: `/${routes.admin}/${routes.incidentMgmt}`,
      name: `Incident Management - ${communityData?.name}`,
    },
    {
      link: "",
      name: !id ? "Add Incident" : `${capitalize(action || "")} Incident`,
    },
  ];

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.incidentMgmt}`);
  };

  useEffect(() => {
    if (incidentData) {
      form.reset({
        [INCIDENT_TYPE]: incidentData?.data?.[INCIDENT_TYPE]?.toString() || "",
        [INCIDENT_CATEGORY]:
          incidentData?.data?.[INCIDENT_CATEGORY]?.toString() || "",
        [PROPERTY_SPECIFIC]: !incidentData?.data?.commonAreaFlag || false,
        [COMMON_AREA]: incidentData?.data?.commonAreaFlag || false,
        [ASSIGN_TO]: incidentData?.data?.occupancyId?.toString() || "",
        [RESPONSIBLE]: incidentData?.data?.responsibleBy?.toString() || "",
        [REPORTEE]: incidentData?.data?.reporterName?.toString() || "",
        [ASSIGNMENT_DATE]: incidentData?.data?.[ASSIGNMENT_DATE]
          ? moment(incidentData?.data?.[ASSIGNMENT_DATE]).format("MM/DD/YYYY")
          : moment().format("MM/DD/YYYY"),
        [PRIORITY]: incidentData?.data?.[PRIORITY]?.toString() || "",
        [STATUS]: incidentData?.data?.[STATUS]?.toString() || "",
        [ATTACHMENTS]: incidentData?.data?.presignedUrls || "",
        [DESCRIPTION]: incidentData?.data?.[DESCRIPTION]?.toString() || "",
        [PROPERTY_ID]: incidentData?.data?.[PROPERTY_ID]?.toString() || "",
        [COMMENTS]: incidentData?.data?.[COMMENTS]?.toString() || "",
      });

      setKey({
        incidentType: Math.random() * 2,
        incidentCategory: Math.random() * 2,
        responsible: Math.random() * 2,
        priority: Math.random() * 2,
        status: Math.random() * 2,
      });
    }
  }, [incidentData]);

  useEffect(() => {
    setKey((prev: any) => ({
      ...prev,
      responsible: Math.random() * 2,
    }));
  }, [propertiesData]);

  const { mutate: addInfo, isPending: isAddPending } = useAddIncidentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast.success("Incident Added Successfully");
      queryClient.invalidateQueries({
        queryKey: ["incident-list"],
      });
      onBackClick();
    },
  });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateIncidentMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Incident Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: ["incident-list"],
        });
        onBackClick();
      },
    });

  const onSubmit = (data: formValues) => {
    let _data = setEmptyFieldsToNull(data);
    _data = removeEmptyFields(_data);

    if (_data[PROPERTY_SPECIFIC] && !_data[ASSIGN_TO]) {
      form.setError(ASSIGN_TO, {
        message: "This field is required",
      });
      return;
    }

    const propertySpecific = _data[PROPERTY_SPECIFIC];
    delete _data[PROPERTY_SPECIFIC];

    _data[ATTACHMENTS] = (_data[ATTACHMENTS] as any)?.filter(
      (_: any) => !_.attachmentId
    );

    _data[ASSIGNMENT_DATE] = moment(_data[ASSIGNMENT_DATE]).format('YYYY-MM-DD')
    
    if (isEdit) {
      updateInfo({
        id: incidentData?.data?.incidentId,
        data: {
          ..._data,
          communityId: communityData?.communityId,
          [COMMON_AREA]: propertySpecific ? false : true,
          [REPORTEE]: loggedInUserData?.userId,
          ...(_data[ATTACHMENTS] ? { [ATTACHMENTS]: _data[ATTACHMENTS] } : {}),
        },
      });
    } else {
      addInfo({
        data: {
          ..._data,
          [STATUS]: "OPEN",
          communityId: communityData?.communityId,
          [COMMON_AREA]: propertySpecific ? false : true,
          [REPORTEE]: loggedInUserData?.userId,
          ...(_data[ATTACHMENTS] ? { [ATTACHMENTS]: _data[ATTACHMENTS] } : {}),
        },
      });
    }
  };

  const onOptionChanges = (value: string) => {
    form.setValue(PROPERTY_SPECIFIC, Boolean(value == "1"));
    form.setValue(COMMON_AREA, Boolean(value == "2"));
  };

  const onDeleteAttachment = (index: number) => () => {
    if (isAdd) {
      form.setValue(
        ATTACHMENTS,
        form.watch(ATTACHMENTS)?.filter((_: any, idx: number) => idx != index)
      );
    }
  };

  const onViewImage = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

  const {
    data: usersGridData,
    isLoading: isUserGridLoading,
    refetch: refetchUserGridData,
    isRefetching: isRefetchingUserData
  } = useGetPropertyAssociationList({
    data: {
      filters: [
        {
          field: "communityId",
          value: communityData?.communityId,
          operator: "EQUAL",
        },
        {
          field: "associationType",
          value: "OWNER",
          operator: "EQUAL",
        },
        {
          field: "active",
          value: true,
          operator: "EQUAL",
        }
      ],
      pageSize: 10,
      pageNumber
    },
    queryParams: {
      enabled: !!(communityData?.communityId)
    }
  });

  useEffect(() => {
    refetchUserGridData();
  }, [pageNumber]);

  useEffect(() => {
    if (Array.isArray(usersGridData?.data)) {
      const incomingOptions = usersGridData.data
        .filter((d: any) => d?.active === true)
        .map((d: any) => {
          const ownerName = `${d?.firstName || ""} ${d?.lastName || ""}`.trim() || d?.userName || d?.username || "";
          const propertyName = d?.propertyName || propertiesData?.data?.find(
            (property: any) => property?.propertyId?.toString() == d?.propertyId?.toString()
          )?.name;
          return {
            label: propertyName ? `${propertyName} (${ownerName})` : ownerName,
            value: d?.ownershipId?.toString(),
          };
        });

      // Check if we reached the last page
      if (usersGridData.data.length < 10) {
        setHasMore(false);
      }

      setUserOptions((prevOptions) => {
        // Reset list on first page
        if (pageNumber === 0) return incomingOptions;

        // Deduplicate using a Map/Set on unique ID values
        const existingIds = new Set(prevOptions.map((opt) => opt.value));
        const filteredNew = incomingOptions.filter((opt: any) => !existingIds.has(opt.value));
        
        return [...prevOptions, ...filteredNew];
      });
    }
  }, [usersGridData, pageNumber, propertiesData]);

  const loadMoreOptions = () => {
    if (!isUserGridLoading && !isRefetchingUserData && hasMore) {
      setPageNumber((prev) => prev + 1);
      setTimeout(() => refetchUserGridData(), 100)
    }
  };

  return {
    isAdd,
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    attachmentRef,
    onOptionChanges,
    incidentCategory,
    isIncidentCategoryLoading,
    incidentType,
    isIncidentTypeLoading,
    propertiesData,
    isLoadingProperties,
    onDeleteAttachment,
    isIncidentDataLoading,
    isView,
    keys,
    isUpdatePending,
    isEdit,
    onViewImage,
    loggedInUserData,
    incidentComments,
    isIncidentCommentsLoading,
    incidentData,
    loadMoreOptions,
    userOptions,
    isUserLoading: isUserGridLoading || isRefetchingUserData,
    hasMore,
  };
};

export default useAddEdit;
